import type { OrgPendingTimeEntry } from "../hooks/usePendingTimeEntries";
import type { OrgApprovedTimeEntry } from "../hooks/useApprovedTimeEntries";
import type { OrgAwaitingSubmissionShift } from "../hooks/useAwaitingSubmissionShifts";

// Base type for entries/shifts that can be grouped (share common fields)
type GroupableEntry =
  | OrgPendingTimeEntry
  | OrgApprovedTimeEntry
  | OrgAwaitingSubmissionShift;

export interface GroupedFacility<
  T extends GroupableEntry = OrgPendingTimeEntry,
> {
  facilityId: string;
  facilityName: string;
  facilityLocation: string;
  entries: T[];
}

export interface GroupedProvider<
  T extends GroupableEntry = OrgPendingTimeEntry,
> {
  providerId: string;
  providerName: string;
  facilities: GroupedFacility<T>[];
  totalEntries: number;
}

/**
 * Groups time entries by provider, then by facility.
 * Returns a hierarchical structure for display in the review table.
 * Works with both pending and approved entries.
 */
export function groupByProviderAndFacility<T extends GroupableEntry>(
  entries: T[],
): GroupedProvider<T>[] {
  const providerMap = new Map<string, GroupedProvider<T>>();

  for (const entry of entries) {
    const providerId = entry.providerId;
    const providerName =
      [entry.providerFirstName, entry.providerLastName]
        .filter(Boolean)
        .join(" ") || "Unknown Provider";

    // Get or create provider group
    if (!providerMap.has(providerId)) {
      providerMap.set(providerId, {
        providerId,
        providerName,
        facilities: [],
        totalEntries: 0,
      });
    }

    const provider = providerMap.get(providerId)!;
    provider.totalEntries++;

    // Find or create facility within provider
    let facility = provider.facilities.find(
      (f) => f.facilityId === entry.facilityId,
    );

    if (!facility) {
      const location = [entry.facilityCity, entry.facilityState]
        .filter(Boolean)
        .join(", ");

      facility = {
        facilityId: entry.facilityId,
        facilityName: entry.facilityName,
        facilityLocation: location,
        entries: [],
      };
      provider.facilities.push(facility);
    }

    facility.entries.push(entry);
  }

  // Convert to array and sort
  const result = Array.from(providerMap.values());

  // Sort providers by name
  result.sort((a, b) => a.providerName.localeCompare(b.providerName));

  // Sort facilities within each provider by name
  for (const provider of result) {
    provider.facilities.sort((a, b) =>
      a.facilityName.localeCompare(b.facilityName),
    );

    // Sort entries within each facility by date (newest first)
    for (const facility of provider.facilities) {
      facility.entries.sort(
        (a, b) =>
          new Date(
            "submittedStartsAt" in b
              ? b.submittedStartsAt
              : b.scheduledStartsAt,
          ).getTime() -
          new Date(
            "submittedStartsAt" in a
              ? a.submittedStartsAt
              : a.scheduledStartsAt,
          ).getTime(),
      );
    }
  }

  return result;
}

/**
 * Calculates the duration in hours between two dates.
 */
export function calculateHours(start: Date, end: Date): number {
  const diff = end.getTime() - start.getTime();
  return diff / (1000 * 60 * 60);
}

/**
 * Formats hours as hours and minutes (e.g., "8 hrs", "55 min", "8 hrs 30 min").
 */
export function formatHours(hours: number): string {
  const absHours = Math.abs(hours);
  const totalMinutes = Math.round(absHours * 60);
  const wholeHours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const sign = hours < 0 ? "-" : "";

  if (wholeHours === 0) {
    return `${sign}${minutes} min`;
  } else if (minutes === 0) {
    return `${sign}${wholeHours} hrs`;
  } else {
    return `${sign}${wholeHours} hrs ${minutes} min`;
  }
}

/**
 * Calculates the difference between submitted and scheduled hours.
 */
export function calculateHoursDifference(
  scheduledStart: Date,
  scheduledEnd: Date,
  submittedStart: Date,
  submittedEnd: Date,
): number {
  const scheduled = calculateHours(scheduledStart, scheduledEnd);
  const submitted = calculateHours(submittedStart, submittedEnd);
  return submitted - scheduled;
}
